/**
 * EventStream - Centralized Event System
 * High-performance pub/sub system with throttling, filtering, and memory leak prevention
 */

import {
    BaseEvent,
    UniversalEvent,
    EventListener,
    EventSubscription,
    EventSubscriptionOptions,
    EventEmissionOptions,
} from '../types/EventTypes';
import { createDebugLogger, DebugLogger } from '../config/PlanetLayerConfig';

/**
 * Performance monitoring for event system
 */
interface EventPerformanceMetrics {
    totalEvents: number;
    totalSubscriptions: number;
    averageEmissionTime: number;
    slowestEvent: { type: string; time: number };
    memoryLeakWarnings: number;
}

/**
 * Internal subscription data
 */
interface InternalSubscription extends EventSubscription {
    lastExecuted: number;
    executionCount: number;
    throttleTimer?: NodeJS.Timeout;
    debounceTimer?: NodeJS.Timeout;
}

/**
 * Centralized event stream for universal architecture
 */
export class EventStream {
    private static instance: EventStream | null = null;
    
    private subscriptions: Map<string, InternalSubscription[]> = new Map();
    private subscriptionCounter = 0;
    private isDestroyed = false;
    private debugMode = false;
    private debugLogger: DebugLogger;
    private performanceMetrics: EventPerformanceMetrics;

    private constructor() {
        this.performanceMetrics = {
            totalEvents: 0,
            totalSubscriptions: 0,
            averageEmissionTime: 0,
            slowestEvent: { type: '', time: 0 },
            memoryLeakWarnings: 0
        };

        // Enable debug mode in development
        if (process.env.NODE_ENV === 'development') {
            this.debugMode = true;
        }
        this.debugLogger = createDebugLogger('EventStream', this.debugMode);

        // Memory leak detection
        this.startMemoryLeakDetection();
    }

    /**
     * Get singleton instance
     */
    public static getInstance(): EventStream {
        if (!EventStream.instance) {
            EventStream.instance = new EventStream();
        }
        return EventStream.instance;
    }

    /**
     * Subscribe to events
     */
    public subscribe<T extends BaseEvent = UniversalEvent>(
        eventType: string,
        listener: EventListener<T>,
        options: EventSubscriptionOptions = {}
    ): EventSubscription {
        if (this.isDestroyed) {
            throw new Error('EventStream has been destroyed');
        }

        const subscriptionId = `sub_${++this.subscriptionCounter}_${Date.now()}`;
        
        const subscription: InternalSubscription = {
            id: subscriptionId,
            eventType,
            listener: listener as unknown as EventListener<UniversalEvent>, // Исправлено: добавлен unknown
            options,
            lastExecuted: 0,
            executionCount: 0,
            unsubscribe: () => this.unsubscribe(subscriptionId, eventType)
        };

        // Initialize subscriptions array for event type if needed
        if (!this.subscriptions.has(eventType)) {
            this.subscriptions.set(eventType, []);
        }

        // Insert subscription based on priority
        const subscriptions = this.subscriptions.get(eventType)!;
        const priority = options.priority || 0;
        const insertIndex = subscriptions.findIndex(sub => (sub.options.priority || 0) < priority);
        
        if (insertIndex === -1) {
            subscriptions.push(subscription);
        } else {
            subscriptions.splice(insertIndex, 0, subscription);
        }

        this.performanceMetrics.totalSubscriptions++;

        //     subscriptionId, 
        //     totalSubs: subscriptions.length,
        //     priority 
        // });

        return subscription;
    }

    /**
     * Unsubscribe from events
     */
    public unsubscribe(subscriptionId: string, eventType: string): void {
        const subscriptions = this.subscriptions.get(eventType);
        if (!subscriptions) {
            return;
        }

        const index = subscriptions.findIndex(sub => sub.id === subscriptionId);
        if (index === -1) {
            return;
        }

        const subscription = subscriptions[index];
        
        // Clear any pending timers
        if (subscription.throttleTimer) {
            clearTimeout(subscription.throttleTimer);
        }
        if (subscription.debounceTimer) {
            clearTimeout(subscription.debounceTimer);
        }

        subscriptions.splice(index, 1);
        this.performanceMetrics.totalSubscriptions--;

        // Clean up empty event type arrays
        if (subscriptions.length === 0) {
            this.subscriptions.delete(eventType);
        }

    }

    /**
     * Emit event to all subscribers
     */
    public emit<T extends BaseEvent>(
        event: T,
        options: EventEmissionOptions = {}
    ): void {
        if (this.isDestroyed) {
            this.debugWarn('Attempted to emit event on destroyed EventStream', { event });
            return;
        }

        const startTime = performance.now();
        const subscriptions = this.subscriptions.get(event.type);

        if (!subscriptions || subscriptions.length === 0) {
            return;
        }

        this.performanceMetrics.totalEvents++;
        
        // Add metadata if provided
        if (options.metadata) {
            event.metadata = { ...event.metadata, ...options.metadata };
        }

        //     event, 
        //     subscriberCount: subscriptions.length 
        // });

        // Process subscriptions
        const processSubscriptions = () => {
            subscriptions.forEach(subscription => {
                try {
                    this.executeListener(subscription, event, options);
                } catch (error) {
                    this.debugError(`Error in event listener for ${event.type}`, { 
                        error, 
                        subscriptionId: subscription.id 
                    });
                }
            });
        };

        if (options.sync) {
            processSubscriptions();
        } else {
            // Use microtask queue for better React batching
            // (microtasks execute before next render, unlike setTimeout)
            queueMicrotask(processSubscriptions);
        }

        // Performance tracking
        const emissionTime = performance.now() - startTime;
        this.updatePerformanceMetrics(event.type, emissionTime);
    }

    /**
     * Execute individual listener with throttling/debouncing
     */
    private executeListener(
        subscription: InternalSubscription,
        event: BaseEvent,
        emissionOptions: EventEmissionOptions
    ): void {
        const { options } = subscription;
        const now = Date.now();

        // Apply source filter
        if (options.sourceFilter) {
            const sourceFilters = Array.isArray(options.sourceFilter) 
                ? options.sourceFilter 
                : [options.sourceFilter];
            
            if (!sourceFilters.includes(event.source)) {
                return;
            }
        }

        // Apply custom filter
        if (options.filter && !options.filter(event as UniversalEvent)) {
            return;
        }

        // Handle once-only subscription
        if (options.once && subscription.executionCount > 0) {
            this.unsubscribe(subscription.id, subscription.eventType);
            return;
        }

        // Handle throttling
        if (options.throttle && !emissionOptions.immediate) {
            const timeSinceLastExecution = now - subscription.lastExecuted;
            if (timeSinceLastExecution < options.throttle) {
                // Skip if within throttle period
                return;
            }
        }

        // Handle debouncing
        if (options.debounce && !emissionOptions.immediate) {
            if (subscription.debounceTimer) {
                clearTimeout(subscription.debounceTimer);
            }
            
            subscription.debounceTimer = setTimeout(() => {
                this.actuallyExecuteListener(subscription, event);
            }, options.debounce);
            return;
        }

        this.actuallyExecuteListener(subscription, event);
    }

    /**
     * Actually execute the listener
     */
    private actuallyExecuteListener(subscription: InternalSubscription, event: BaseEvent): void {
        try {
            subscription.lastExecuted = Date.now();
            subscription.executionCount++;

            const result = subscription.listener(event as UniversalEvent);
            
            // Handle async listeners
            if (result instanceof Promise) {
                result.catch(error => {
                    this.debugError(`Async listener error for ${event.type}`, { 
                        error, 
                        subscriptionId: subscription.id 
                    });
                });
            }

        } catch (error) {
            this.debugError(`Listener execution error for ${event.type}`, { 
                error, 
                subscriptionId: subscription.id 
            });
        }
    }

    /**
     * Get all active subscriptions (for debugging)
     */
    public getActiveSubscriptions(): Record<string, number> {
        const result: Record<string, number> = {};
        this.subscriptions.forEach((subs, eventType) => {
            result[eventType] = subs.length;
        });
        return result;
    }

    /**
     * Get performance metrics
     */
    public getPerformanceMetrics(): EventPerformanceMetrics {
        return { ...this.performanceMetrics };
    }

    /**
     * Clear all subscriptions for an event type
     */
    public clearEventType(eventType: string): void {
        const subscriptions = this.subscriptions.get(eventType);
        if (subscriptions) {
            // Clear all timers
            subscriptions.forEach(sub => {
                if (sub.throttleTimer) clearTimeout(sub.throttleTimer);
                if (sub.debounceTimer) clearTimeout(sub.debounceTimer);
            });
            
            this.subscriptions.delete(eventType);
            this.performanceMetrics.totalSubscriptions -= subscriptions.length;
            
            //     clearedCount: subscriptions.length 
            // });
        }
    }

    /**
     * Clear all subscriptions (for cleanup)
     */
    public clearAll(): void {
        this.subscriptions.forEach((subscriptions, eventType) => {
            subscriptions.forEach(sub => {
                if (sub.throttleTimer) clearTimeout(sub.throttleTimer);
                if (sub.debounceTimer) clearTimeout(sub.debounceTimer);
            });
        });
        
        // const totalCleared = this.performanceMetrics.totalSubscriptions;
        this.subscriptions.clear();
        this.performanceMetrics.totalSubscriptions = 0;
        
    }

    /**
     * Destroy the event stream
     */
    public destroy(): void {
        if (this.isDestroyed) {
            return;
        }

        this.clearAll();
        this.isDestroyed = true;
        EventStream.instance = null;
        
    }

    /**
     * Enable/disable debug mode
     */
    public setDebugMode(enabled: boolean): void {
        this.debugMode = enabled;
    }

    // Private helper methods

    private updatePerformanceMetrics(eventType: string, emissionTime: number): void {
        const metrics = this.performanceMetrics;
        
        // Update average emission time
        metrics.averageEmissionTime = (
            (metrics.averageEmissionTime * (metrics.totalEvents - 1)) + emissionTime
        ) / metrics.totalEvents;

        // Track slowest event
        if (emissionTime > metrics.slowestEvent.time) {
            metrics.slowestEvent = { type: eventType, time: emissionTime };
        }

        // Warn about slow events
        if (emissionTime > 10) { // 10ms threshold
            this.debugWarn(`Slow event emission detected: ${eventType}`, { 
                emissionTime: `${emissionTime.toFixed(2)}ms` 
            });
        }
    }

    private startMemoryLeakDetection(): void {
        if (process.env.NODE_ENV !== 'development') {
            return;
        }

        setInterval(() => {
            const totalSubs = this.performanceMetrics.totalSubscriptions;
            
            if (totalSubs > 1000) { // Arbitrary threshold
                this.performanceMetrics.memoryLeakWarnings++;
                this.debugWarn(`High subscription count detected: ${totalSubs}`, {
                    subscriptionsByType: this.getActiveSubscriptions()
                });
            }
        }, 30000); // Check every 30 seconds
    }

    private debugLog(message: string, data?: any): void {
        this.debugLogger.log(message, data);
    }

    private debugWarn(message: string, data?: any): void {
        this.debugLogger.warn(message, data);
    }

    private debugError(message: string, data?: any): void {
        this.debugLogger.error(message, data);
    }
}

/**
 * Global event stream instance
 */
export const globalEventStream = EventStream.getInstance();

/**
 * Convenience function to create typed events
 */
export function createEvent<T extends BaseEvent>(
    type: string,
    source: string,
    data: Omit<T, keyof BaseEvent>
): T {
    return {
        id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type,
        source,
        timestamp: Date.now(),
        ...data
    } as T;
}

/**
 * Convenience function for quick event emission
 */
export function emit<T extends BaseEvent>(
    type: string,
    source: string,
    data: Omit<T, keyof BaseEvent>,
    options?: EventEmissionOptions
): void {
    const event = createEvent<T>(type, source, data);
    globalEventStream.emit(event, options);
}

/**
 * Chart navigation event helpers
 */
export function emitChartActivated(
    chartId: string,
    previousChartId: string | undefined,
    activationType: 'tab-click' | 'url-change' | 'programmatic' = 'tab-click',
    source: string = 'chart-navigation'
): void {
    const event = createEvent('chart:activated', source, {
        chartId,
        previousChartId,
        activationData: {
            activationType,
            timestamp: Date.now()
        }
    });
    globalEventStream.emit(event);
}

export function emitChartDeactivated(
    chartId: string,
    source: string = 'chart-navigation'
): void {
    const event = createEvent('chart:deactivated', source, {
        chartId
    });
    globalEventStream.emit(event);
}

// Export globalEventStream to window for debugging
// NOTE: ChakraEventSequenceManager initialization moved to App.tsx to avoid circular dependency
if (typeof window !== 'undefined') {
    (window as any).globalEventStream = globalEventStream;
}
